import { motion } from 'framer-motion';
import { LightState } from '../../types/protocol';

interface TrafficLightProps {
  red: LightState;
  amber: LightState;
  green: LightState;
  redError?: boolean;
  amberError?: boolean;
  greenError?: boolean;
  size?: 'sm' | 'md' | 'lg';
  orientation?: 'vertical' | 'horizontal';
  label?: string;
}

const sizeMap = {
  sm: { light: 16, gap: 2, padding: 4 },
  md: { light: 24, gap: 4, padding: 6 },
  lg: { light: 32, gap: 6, padding: 8 }
};

const colorMap = {
  red: { on: '#ef4444', off: '#450a0a', error: '#fca5a5' },
  amber: { on: '#f59e0b', off: '#451a03', error: '#fcd34d' },
  green: { on: '#22c55e', off: '#052e16', error: '#86efac' }
};

function Light({
  color,
  state,
  hasError,
  size
}: {
  color: 'red' | 'amber' | 'green';
  state: LightState;
  hasError: boolean;
  size: number;
}) {
  const colors = colorMap[color];
  const isOn = state === LightState.ON;
  const isFlashing = state === LightState.FLASHING;
  const isFlashingFast = state === LightState.FLASHING_FAST;

  const baseColor = hasError ? colors.error : (isOn || isFlashing || isFlashingFast) ? colors.on : colors.off;

  return (
    <motion.div
      className="rounded-full relative"
      style={{
        width: size,
        height: size,
        backgroundColor: baseColor,
        boxShadow: (isOn || isFlashing || isFlashingFast)
          ? `0 0 ${size/2}px ${baseColor}`
          : 'inset 0 2px 4px rgba(0,0,0,0.3)'
      }}
      animate={
        isFlashing ? { opacity: [1, 0.2, 1] } :
        isFlashingFast ? { opacity: [1, 0.2, 1] } :
        { opacity: 1 }
      }
      transition={
        isFlashing ? { duration: 1, repeat: Infinity, ease: 'easeInOut' } :
        isFlashingFast ? { duration: 0.5, repeat: Infinity, ease: 'easeInOut' } :
        { duration: 0.2 }
      }
    >
      {hasError && (
        <div
          className="absolute inset-0 flex items-center justify-center text-black font-bold"
          style={{ fontSize: size * 0.5 }}
        >
          !
        </div>
      )}
    </motion.div>
  );
}

export function TrafficLight({
  red,
  amber,
  green,
  redError = false,
  amberError = false,
  greenError = false,
  size = 'md',
  orientation = 'vertical',
  label
}: TrafficLightProps) {
  const dims = sizeMap[size];
  const isVertical = orientation === 'vertical';

  return (
    <div className="flex flex-col items-center gap-1">
      <div
        className="bg-gray-800 rounded-lg flex items-center justify-center"
        style={{
          flexDirection: isVertical ? 'column' : 'row',
          gap: dims.gap,
          padding: dims.padding,
          border: '2px solid #374151'
        }}
      >
        <Light color="red" state={red} hasError={redError} size={dims.light} />
        <Light color="amber" state={amber} hasError={amberError} size={dims.light} />
        <Light color="green" state={green} hasError={greenError} size={dims.light} />
      </div>
      {label && (
        <span className="text-xs text-gray-400 font-mono">{label}</span>
      )}
    </div>
  );
}

// Pedestrian traffic light (only red and green)
export function PedestrianLight({
  red,
  green,
  redError = false,
  greenError = false,
  size = 'md',
  label
}: Omit<TrafficLightProps, 'amber' | 'amberError' | 'orientation'>) {
  const dims = sizeMap[size];

  return (
    <div className="flex flex-col items-center gap-1">
      <div
        className="bg-gray-800 rounded-lg flex flex-col items-center justify-center"
        style={{
          gap: dims.gap,
          padding: dims.padding,
          border: '2px solid #374151'
        }}
      >
        <Light color="red" state={red} hasError={redError} size={dims.light} />
        <Light color="green" state={green} hasError={greenError} size={dims.light} />
      </div>
      {label && (
        <span className="text-xs text-gray-400 font-mono">{label}</span>
      )}
    </div>
  );
}
